import { useFormatters } from '@/app/hooks/useFormatters'
import { ROUTES } from '@/app/lib/constants'
import { ICourse } from '@/types/course'
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import { Chip } from '@mui/material'
import cn from 'classnames'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import React, { ComponentProps, memo, useEffect, useState } from 'react'
import { ImageWithFallback } from '../../shared/components/image-with-fallback'

export type NewCourseCardProps = React.ComponentProps<'article'> & {
  course: Pick<
    ICourse,
    | 'name'
    | 'learningDescription'
    | '_id'
    | 'educationProvider'
    | 'instructors'
    | 'cohorts'
    | 'tags'
    | 'level'
  >
  variant?: 'compact' | 'full'
  prefix?: React.ReactNode
}

function NewCourseCard({
  course,
  prefix,
  variant = 'full',
  ...props
}: NewCourseCardProps) {
  const t = useTranslations('NewCourseCard')
  const { _id, name, learningDescription, level } = course

  const { formatCourseDuration } = useFormatters()
  const courseDuration = formatCourseDuration(course)

  const LinkToCourse = ({ children, ...props }) => (
    <Link href={ROUTES.course.replace(':id', _id)} {...props}>
      {children}
    </Link>
  )

  return (
    <article
      {...props}
      className={`NewCourseCard group rounded-lg border border-lines bg-mydra-white text-mydra-black hover:border-mydra-black ${props.className ?? ''}`}
      data-variant={variant}
    >
      <LinkToCourse>
        <div className="flex h-full flex-col gap-6 p-4">
          <header className="flex flex-col gap-4">
            <h4 className="inline-flex items-center gap-2 font-title text-[1.375rem] text-black">
              {prefix && variant === 'compact' && (
                <span className="text-3xl font-bold text-mydra-mouse">
                  {prefix}
                </span>
              )}
              <span className="line-clamp-2 text-ellipsis">{name}</span>
            </h4>
            {variant === 'full' && (
              <div className="flex items-center gap-2">
                {courseDuration && (
                  <Chip
                    className="!bg-mydra-medium-gray/40"
                    size="small"
                    icon={<AccessAlarmIcon className="text-inherit" />}
                    label={
                      courseDuration.at(0)?.toUpperCase() +
                      courseDuration.slice(1)
                    }
                  ></Chip>
                )}
                {level && (
                  <Chip
                    className={cn(
                      level === 'beginner'
                        ? 'bg-success-light text-success-dark'
                        : level === 'intermediate'
                          ? 'bg-warning-light/50 text-warning'
                          : level === 'advanced'
                            ? 'bg-mydra-purple-light text-mydra-purple'
                            : `!bg-mydra-medium-gray/40`
                    )}
                    size="small"
                    icon={<LayersOutlinedIcon className="text-inherit" />}
                    label={level?.charAt(0)?.toUpperCase() + level?.slice(1)}
                  ></Chip>
                )}
              </div>
            )}
          </header>
          <div className="mt-auto flex flex-col gap-4">
            <CourseInstructors course={course} />
            {variant === 'full' && learningDescription && (
              <p className="line-clamp-2 text-ellipsis text-mydra-black">
                {learningDescription}
              </p>
            )}
          </div>
          {variant === 'full' && (
            <footer className="mt-auto flex items-baseline justify-stretch gap-6">
              <div className="mt-auto w-full rounded-xl border border-lines px-4 py-3 text-center text-base leading-6 text-mydra-black group-hover:!bg-mydra-gray">
                {t('learn_more_and_enroll')}
              </div>
            </footer>
          )}
        </div>
      </LinkToCourse>
    </article>
  )
}

type CourseInstructorsProps = ComponentProps<'div'> & {
  course: Pick<ICourse, '_id' | 'instructors' | 'educationProvider'>
}

function CourseInstructors({ course, ...props }: CourseInstructorsProps) {
  const t = useTranslations('CourseInstructors')
  const { instructors, educationProvider } = course
  // TODO: i18n
  const validInstructors = (instructors ?? []).filter(
    (instructor) => instructor && instructor.name && instructor.avatarUrl
  )
  const { logoUrl, name = '' } = educationProvider

  const [instructor] = validInstructors
  const images = [instructor?.avatarUrl, logoUrl].filter(Boolean)
  const instructorByLine = [instructor?.name, instructor?.role]
    .filter(Boolean)
    .join(', ')

  const [loadedCount, setLoadedCount] = useState(0)
  const totalImages = images.length
  const isLoading = totalImages > 0 && loadedCount < totalImages

  useEffect(() => {
    if (totalImages === 0 || !isLoading) {
      return
    }

    const timeoutId = setTimeout(() => {
      setLoadedCount(totalImages)
    }, 30000)

    return () => clearTimeout(timeoutId)
  }, [totalImages, isLoading])

  const handleImageComplete = () => {
    setLoadedCount((prev) => prev + 1)
  }

  return (
    <div
      {...props}
      className={`relative flex items-center gap-3 ${props.className ?? ''}`}
    >
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center gap-3 bg-mydra-white">
          <div className="flex shrink-0">
            <div className="h-10 w-10 animate-pulse rounded-full bg-mydra-mouse" />
            {totalImages > 1 && (
              <div className="-ml-2 h-10 w-10 animate-pulse rounded-full bg-mydra-mouse" />
            )}
          </div>
          <div className="flex flex-col gap-1">
            <div className="h-4 w-32 animate-pulse rounded bg-mydra-mouse" />
            <div className="h-4 w-24 animate-pulse rounded bg-mydra-mouse" />
          </div>
        </div>
      )}
      <div className="flex shrink-0 empty:hidden">
        {images.map((imageUrl, index) => (
          <ImageWithFallback
            key={`instructor-image-${index}-${course?._id ?? ''}`}
            className="inline-block h-10 w-10 shrink-0 rounded-full border border-divider-light object-cover [&:not(:first-child)]:-ml-2"
            fallback={null}
            src={imageUrl}
            height={40}
            width={40}
            alt={t('instructor_image')}
            onLoad={handleImageComplete}
            onError={handleImageComplete}
          />
        ))}
      </div>
      <div className="flex">
        <p className="leading-[140%] text-mydra-black" title={instructorByLine}>
          <span className="line-clamp-1 capitalize">{instructorByLine}</span>
          <span className="text-[--text]">{name}</span>
        </p>
      </div>
    </div>
  )
}

const MemoizedCourseCard = memo(NewCourseCard)

export { MemoizedCourseCard as NewCourseCard }
